import React, { useEffect, useState } from "react";
import { useAdmin } from "../shared/AdminContext";
import ExitSurvey from "../components/ExitSurvey";
import { ChevronDown, Moon, Bell, Globe, Shield, MapPin, CreditCard, LogOut, Compass, Sparkles, Check, Loader2, X, Building2, ArrowRight, Fingerprint } from "lucide-react";
import { GOLD } from "../constants/theme";
import { ADDONS, ADDON_KEYS, isAllowedBetaUser } from "../constants/addons";
import { saveUserData } from "../utils/userData";
import { useUserAddons } from "../utils/useUserAddons";
import { useOrg } from "../shared/OrgContext";
import { CONSULTING_TIERS, consultingLockedToAllowlist } from "../constants/consulting";
import { PartnerOrgBadge } from "../components/PartnerOrgBadge";
import { supabase } from "../../supabaseClient";
import { isNative } from "../../utils/platform";
import { biometricAvailable, biometricVerify, haptic } from "../../utils/nativeBridge";
import { bamConfirm, bamToast } from "../shared/dialog";
import BamLoading from "../shared/BamLoading";
import { assertNotStaging } from "../../config/environment";

const IANA_TIMEZONES=["Africa/Cairo","Africa/Johannesburg","Africa/Lagos","Africa/Nairobi","America/Anchorage","America/Argentina/Buenos_Aires","America/Bogota","America/Chicago","America/Denver","America/Halifax","America/Los_Angeles","America/Mexico_City","America/New_York","America/Phoenix","America/Santiago","America/Sao_Paulo","America/St_Johns","America/Toronto","America/Vancouver","Asia/Bangkok","Asia/Colombo","Asia/Dubai","Asia/Hong_Kong","Asia/Istanbul","Asia/Jakarta","Asia/Karachi","Asia/Kolkata","Asia/Manila","Asia/Seoul","Asia/Shanghai","Asia/Singapore","Asia/Taipei","Asia/Tehran","Asia/Tokyo","Atlantic/Reykjavik","Australia/Adelaide","Australia/Brisbane","Australia/Melbourne","Australia/Perth","Australia/Sydney","Europe/Amsterdam","Europe/Athens","Europe/Berlin","Europe/Brussels","Europe/Dublin","Europe/Helsinki","Europe/Lisbon","Europe/London","Europe/Madrid","Europe/Moscow","Europe/Oslo","Europe/Paris","Europe/Prague","Europe/Rome","Europe/Stockholm","Europe/Vienna","Europe/Warsaw","Europe/Zurich","Pacific/Auckland","Pacific/Fiji","Pacific/Guam","Pacific/Honolulu"];

function getTzAbbr(tz){try{return new Intl.DateTimeFormat("en-US",{timeZone:tz,timeZoneName:"short"}).formatToParts(new Date()).find(p=>p.type==="timeZoneName")?.value||"";}catch{return "";}}

export function fmtTimeInTz(date,tz){
  try{
    const d=new Date(typeof date==="number"?date:date);
    const parts=new Intl.DateTimeFormat("en-US",{timeZone:tz,hour:"numeric",minute:"2-digit",hour12:true,timeZoneName:"short"}).formatToParts(d);
    return parts.map(p=>p.value).join("");
  }catch{return "";}
}

// Day-of-week (long) of `date` in `tz`. The hardcoded "Monday" in the
// call widget breaks for users east of NY where Monday 7 PM ET = Tuesday
// morning local — derive dynamically instead.
export function fmtDayInTz(date,tz){
  try{return new Intl.DateTimeFormat("en-US",{timeZone:tz,weekday:"long"}).format(new Date(date));}
  catch{return "";}
}

// Full "Monday, May 11" style date in `tz`. Used in the expanded call
// panel so the date text matches the user's local view of the instant.
export function fmtFullDateInTz(date,tz){
  try{return new Intl.DateTimeFormat("en-US",{timeZone:tz,weekday:"long",month:"short",day:"numeric"}).format(new Date(date));}
  catch{return "";}
}

export function SettingsPage({C,dark,toggleDark,notifs,updateNotif,tz,updateTz,mapVisible,toggleMapVisible,dismissBanner,dismissedBanners,onSignOut,onStartTour,emailPrefs,updateEmailPref}){
  const { isAdmin } = useAdmin();
  // Per-device landing preference; see the boot effect in BAMFull.
  const [adminHome, setAdminHome] = useState(() => {
    try { return localStorage.getItem("bam_admin_home") === "1"; } catch { return false; }
  });
  const [tzSearch, setTzSearch] = useState("");
  const [tzOpen, setTzOpen] = useState(false);
  const [savedKey, setSavedKey] = useState(null);
  const showSaved=(key)=>{setSavedKey(key);setTimeout(()=>setSavedKey(null),1500);};

  // Account deletion (Apple Guideline 5.1.1(v) — required for sign-up apps).
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const doDeleteAccount = async () => {
    setDeleteBusy(true); setDeleteError(null);
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL || "https://rlfekxctjyvozujzbzwm.supabase.co"}/functions/v1/delete-account`,
        { method:"POST", headers:{ "Content-Type":"application/json", ...(token?{Authorization:`Bearer ${token}`}:{}) } }
      );
      if (!res.ok) { const d = await res.json().catch(()=>({})); throw new Error(d?.error || `Delete failed (${res.status})`); }
      // Account gone — clear local state and bounce to the signed-out screen.
      try { await supabase.auth.signOut(); } catch {}
      try { localStorage.clear(); } catch {}
      window.location.hash = "";
      window.location.reload();
    } catch (e) {
      setDeleteError(e?.message || "Couldn't delete your account. Email support@byanymeanscoaches.com.");
      setDeleteBusy(false);
    }
  };

  // Push notification preferences (notification_prefs table). Self-contained:
  // loads the signed-in user's row on mount, upserts on toggle. A missing row
  // or null column falls back to the category default (mirrors push.ts).
  const PUSH_CATEGORIES = [
    { key:"chat_messages",     label:"Messages",                desc:"New messages in your chats (mute a single chat from inside it)", def:true },
    { key:"community_replies", label:"Replies & mentions",      desc:"When someone replies to you or @mentions you", def:true },
    { key:"community_all",     label:"All community activity",  desc:"Every new community post (can get noisy)",      def:false },
    { key:"going_live",        label:"When we go live",         desc:"A session is starting right now",                def:true },
    { key:"live_calls",        label:"Live call starting soon", desc:"An hour before a scheduled call, and as it starts", def:true },
    // Key unchanged (it's the column); the Wednesday Drop and Weekly Q&A
    // ride it too, so it's named for what it covers now.
    { key:"new_masterclass",   label:"New content",             desc:"The Wednesday Drop, new masterclasses and Q&As", def:true },
    { key:"weekly_digest",     label:"Monday check-in",         desc:"Once a week: where you are and what's new",      def:true },
    { key:"new_workouts",      label:"New workouts",            desc:"When a new BAM workout is published",            def:true },
    { key:"cert_progress",     label:"Certification nudges",    desc:"Up to three reminders to finish your certification", def:true },
    { key:"drill_of_day",      label:"Daily drill",             desc:"One drill each morning to work on",              def:false },
    { key:"set_of_day",        label:"Set of the day",          desc:"One set or action each morning, for team coaches", def:false },
    { key:"upload_review",     label:"Your uploads",            desc:"When a video you submitted is approved or sent back", def:true },
  ];
  const [pushPrefs, setPushPrefs] = useState(null); // null = loading
  const [pushUserId, setPushUserId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data:{ user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        setPushUserId(user.id);
        const { data } = await supabase.from("notification_prefs").select("*").eq("user_id", user.id).maybeSingle();
        if (!cancelled) setPushPrefs(data || {});
      } catch { if (!cancelled) setPushPrefs({}); }
    })();
    return () => { cancelled = true; };
  }, []);
  const pushVal = (cat) => {
    const v = pushPrefs?.[cat.key];
    return (v === undefined || v === null) ? cat.def : v === true;
  };
  const updatePushPref = async (key, val) => {
    setPushPrefs(p => ({ ...(p||{}), [key]: val }));
    if (!pushUserId) return;
    try { await supabase.from("notification_prefs").upsert({ user_id: pushUserId, [key]: val }, { onConflict: "user_id" }); } catch {}
  };

  const filteredTz=tzSearch?IANA_TIMEZONES.filter(t=>t.toLowerCase().includes(tzSearch.toLowerCase())):IANA_TIMEZONES;

  const sectionLabel = (text, Icon) => (
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
      {Icon && <Icon size={14} color={GOLD} style={{opacity:0.7}}/>}
      <span style={{fontSize:11,fontWeight:800,color:C.textDim,letterSpacing:1.5,textTransform:"uppercase"}}>{text}</span>
    </div>
  );

  const card = (children) => (
    <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"4px 20px",boxShadow:C.surfaceShadow,marginBottom:24}}>{children}</div>
  );

  const Toggle=({checked,onChange,label,sKey,badge,desc})=>(
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",minHeight:44,borderBottom:`1px solid ${C.border}`}}>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:14,color:C.text,fontWeight:500}}>{label}</span>
          {badge&&<span style={{fontSize:9,fontWeight:700,color:GOLD,background:dark?"rgba(226,221,159,0.12)":"rgba(226,221,159,0.3)",padding:"2px 8px",borderRadius:10,letterSpacing:.5}}>{badge}</span>}
          {savedKey===sKey&&<span style={{fontSize:11,color:GOLD,fontWeight:600,animation:"bamFadeOut 1.5s forwards"}}>Saved</span>}
        </div>
        {desc&&<div style={{fontSize:11,color:C.textDim,marginTop:3}}>{desc}</div>}
      </div>
      <div onClick={()=>{onChange(!checked);showSaved(sKey);}}
        style={{width:44,height:24,borderRadius:12,background:checked?GOLD:dark?"#333":C.border,position:"relative",flexShrink:0,cursor:"pointer",transition:"background .15s"}}>
        <div style={{position:"absolute",top:4,left:4,width:15,height:15,borderRadius:"50%",background:checked?(dark?"#0A0A0A":"#fff"):(dark?"#666":"#fff"),transform:checked?"translateX(20px)":"translateX(0)",transition:"transform .15s",boxShadow:"0 1px 5px rgba(0,0,0,.3)"}}/>
      </div>
    </div>
  );

  return (
    <div className="bam-settings-scroll" style={{flex:1,overflow:"auto",overflowX:"hidden",padding:"28px 28px 60px",display:"flex",justifyContent:"center"}}>
      <div style={{width:"100%",maxWidth:520}}>

        {/* Header */}
        <div style={{marginBottom:32,textAlign:"center"}}>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:36,color:C.text,letterSpacing:2}}>SETTINGS</div>
          <div style={{fontSize:13,color:C.textDim,marginTop:4}}>Manage your preferences and account</div>
        </div>

        {/* Appearance */}
        {sectionLabel("Appearance", Moon)}
        {card(
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",minHeight:44}}>
            <span style={{fontSize:14,color:C.text,fontWeight:500}}>Dark Mode</span>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {savedKey==="dark"&&<span style={{fontSize:11,color:GOLD,fontWeight:600,animation:"bamFadeOut 1.5s forwards"}}>Saved</span>}
              <div onClick={()=>{toggleDark();showSaved("dark");}}
                style={{width:44,height:24,borderRadius:12,background:dark?GOLD:C.border,position:"relative",cursor:"pointer",transition:"background .15s"}}>
                <div style={{position:"absolute",top:4,left:4,width:15,height:15,borderRadius:"50%",background:dark?"#0A0A0A":"#fff",transform:dark?"translateX(20px)":"translateX(0)",transition:"transform .15s",boxShadow:"0 1px 5px rgba(0,0,0,.3)"}}/>
              </div>
            </div>
          </div>
        )}

        {/* Admin-only: land on Admin · Today instead of the coach dashboard.
            Per-device (localStorage), and it yields to push taps and deep
            links — see the boot effect in BAMFull. Deliberately a toggle
            rather than forced: whoever runs admin is also the person who
            catches coach-facing regressions, and that only happens if the
            coach view is still what opens by default. */}
        {isAdmin && (<>
          {sectionLabel("Admin", Bell)}
          {card(
            <Toggle
              checked={adminHome}
              onChange={(v) => {
                setAdminHome(v);
                // Two writes on purpose. localStorage is what the boot
                // redirect reads, because that decision has to be made
                // synchronously before anything renders. user_data is what
                // makes the choice follow the account, so flipping it on
                // the laptop also changes the phone — BAMFull mirrors it
                // back down into localStorage on the next launch.
                try { localStorage.setItem("bam_admin_home", v ? "1" : "0"); } catch {}
                saveUserData("admin_home", v).catch(() => {});
                showSaved("adminHome");
              }}
              label="Open the app to Admin · Today"
              sKey="adminHome"
              desc="Applies to every device you sign in on. A notification tap still wins."
            />
          )}
        </>)}

        {/* The old "In-app notifications" and "Call reminders: 24 hours and 1
            hour before" toggles were saved on the device and switched nothing
            on: real call reminders go 60 and 10 minutes before, set under
            Push notifications below. Removed 2026-10-09. */}

        {/* Push notifications — per-category, stored in notification_prefs.
            Delivered on the BAM iOS app; shown here so prefs sync everywhere. */}
        {sectionLabel("Push notifications", Bell)}
        {card(
          pushPrefs === null ? (
            <BamLoading pad={14}/>
          ) : (
            <>
              {PUSH_CATEGORIES.map(cat => (
                <Toggle key={cat.key} checked={pushVal(cat)} onChange={v=>updatePushPref(cat.key,v)} label={cat.label} sKey={`push_${cat.key}`} desc={cat.desc}/>
              ))}
              <div style={{fontSize:11,color:C.textDim,marginTop:8,lineHeight:1.5}}>Push notifications are delivered on the BAM iOS app.</div>
            </>
          )
        )}

        {/* Email preferences - stored on profiles.email_prefs. Missing = opted in.
            Essential mail (exam reviews, video reviews, billing) always sends. */}
        {sectionLabel("Email", Bell)}
        {card(<>
          <Toggle checked={emailPrefs?.welcome !== false} onChange={v=>updateEmailPref&&updateEmailPref("welcome",v)} label="Welcome + onboarding" sKey="em_welcome" desc="Account setup + getting-started tips"/>
          <Toggle checked={emailPrefs?.cert_updates !== false} onChange={v=>updateEmailPref&&updateEmailPref("cert_updates",v)} label="Certification updates" sKey="em_cert" desc="Submission confirmations and reminders"/>
          <Toggle checked={emailPrefs?.video_updates !== false} onChange={v=>updateEmailPref&&updateEmailPref("video_updates",v)} label="Drill submission updates" sKey="em_video" desc="Status of your submitted drills/videos"/>
          <Toggle checked={emailPrefs?.drops !== false} onChange={v=>updateEmailPref&&updateEmailPref("drops",v)} label="Wednesday Drop" sKey="em_drops" desc="This week's drop, and a short follow-up on Saturday"/>
          <Toggle checked={emailPrefs?.community_replies !== false} onChange={v=>updateEmailPref&&updateEmailPref("community_replies",v)} label="Community replies" sKey="em_reply" desc="When someone replies to your post"/>
          <div style={{fontSize:11,color:C.textDim,marginTop:8,lineHeight:1.5}}>Critical emails (exam review results, drill review results, billing) always send regardless of these settings.</div>
        </>)}

        {/* Timezone */}
        {sectionLabel("Timezone", Globe)}
        <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"14px 20px",boxShadow:C.surfaceShadow,marginBottom:24,position:"relative"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div onClick={()=>setTzOpen(o=>!o)}
              style={{flex:1,display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",minHeight:32}}>
              <div>
                <span style={{fontSize:14,color:C.text,fontWeight:500}}>{tz.replace(/_/g," ")}</span>
                <span style={{fontSize:12,color:GOLD,fontWeight:600,marginLeft:8}}>{getTzAbbr(tz)}</span>
              </div>
              <ChevronDown size={14} color={C.textDim} style={{transition:"transform .2s",transform:tzOpen?"rotate(180deg)":"rotate(0)"}}/>
            </div>
            {savedKey==="tz"&&<span style={{fontSize:11,color:GOLD,fontWeight:600,animation:"bamFadeOut 1.5s forwards",flexShrink:0}}>Saved</span>}
          </div>
          {tzOpen&&(
            <div style={{marginTop:10}}>
              <input value={tzSearch} onChange={e=>setTzSearch(e.target.value)} placeholder="Search timezones..."
                style={{width:"100%",padding:"10px 14px",borderRadius:8,border:`1px solid ${C.border}`,
                  background:dark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.04)",color:C.text,fontSize:13,outline:"none",marginBottom:8,boxSizing:"border-box",fontFamily:"inherit"}}/>
              <div style={{maxHeight:200,overflowY:"auto",borderRadius:8,border:`1px solid ${C.border}`}}>
                {filteredTz.map(t=>(
                  <div key={t} onClick={()=>{updateTz(t);setTzOpen(false);setTzSearch("");showSaved("tz");}}
                    style={{padding:"10px 14px",fontSize:13,color:t===tz?GOLD:C.text,fontWeight:t===tz?700:400,
                      background:t===tz?(dark?"rgba(226,221,159,0.08)":"rgba(226,221,159,0.15)"):"transparent",
                      cursor:"pointer",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span>{t.replace(/_/g," ")}</span>
                    <span style={{fontSize:11,color:C.textDim}}>{getTzAbbr(t)}</span>
                  </div>
                ))}
                {filteredTz.length===0&&<div style={{padding:"14px",fontSize:13,color:C.textDim,textAlign:"center"}}>No matching timezone</div>}
              </div>
            </div>
          )}
        </div>

        {/* Privacy */}
        {sectionLabel("Privacy", MapPin)}
        {card(
          <Toggle checked={mapVisible} onChange={v=>toggleMapVisible(v)} label="Show me on the community map" sKey="map" desc="Other coaches can see your general location"/>
        )}

        {/* Security — native-only Face ID / Touch ID re-auth toggle.
            On web this section is suppressed entirely. */}
        {isNative() && (
          <>
            {sectionLabel("Security", Fingerprint)}
            {card(<SecurityPanel C={C} dark={dark}/>)}
          </>
        )}

        {/* Subscription — unified panel showing plan + add-ons.
            See SubscriptionPanel below for the merged layout.

            Suppressed on native (iOS app): Apple's "reader app" rules
            forbid displaying prices or in-app links to external
            checkout / billing portals. Coaches manage billing on the
            web. We surface a slim status-only card instead. */}
        {sectionLabel("Subscription", CreditCard)}
        {isNative()
          ? <NativeSubscriptionStatus C={C} GOLD={GOLD}/>
          : <SubscriptionPanel C={C} GOLD={GOLD}/>}
        {false && (
        <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"20px",boxShadow:C.surfaceShadow,marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:42,height:42,borderRadius:11,background:`${GOLD}15`,border:`1px solid ${GOLD}30`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Shield size={20} color={GOLD}/>
              </div>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:C.text}}>BAM Coaches Platform</div>
                <div style={{fontSize:12,color:"#5AB584",fontWeight:600,marginTop:3,display:"flex",alignItems:"center",gap:5}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:"#5AB584",display:"inline-block"}}/>
                  Active
                </div>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:"'Bebas Neue',sans-serif",letterSpacing:1}}>$34.99<span style={{fontSize:12,color:C.textDim,fontWeight:400}}>/mo</span></div>
            </div>
          </div>

          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14,display:"flex",flexDirection:"column",gap:8}}>
            {[["Payment","Via Stripe"],["Billing","Monthly"],["Cancel","Anytime \u2014 keeps access through billing period"]].map(([k,v])=>(
              <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:13}}>
                <span style={{color:C.textDim}}>{k}</span>
                <span style={{color:C.textMid,fontWeight:500}}>{v}</span>
              </div>
            ))}
          </div>

          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14,marginTop:12,display:"flex",gap:10}}>
            <button
              onClick={async ()=>{
                try{
                  assertNotStaging("The billing portal");
                  const {data:{session}}=await (await import("../../supabaseClient")).supabase.auth.getSession();
                  const token=session?.access_token;
                  const res=await fetch(
                    process.env.REACT_APP_PORTAL_URL||"https://rlfekxctjyvozujzbzwm.supabase.co/functions/v1/create-portal-session",
                    {method:"POST",headers:{"Content-Type":"application/json",...(token?{Authorization:`Bearer ${token}`}:{})},
                      body:JSON.stringify({returnUrl:window.location.href})}
                  );
                  if(res.ok){const{url}=await res.json();if(url){window.location.href=url;return;}bamToast("Couldn't open the billing portal right now. Please try again in a moment \u2014 if it keeps happening, email support@byanymeanscoaches.com.", { tone: "error" });}
                  else{bamToast("Couldn't open the billing portal right now. Please try again in a moment \u2014 if it keeps happening, email support@byanymeanscoaches.com.", { tone: "error" })}
                }catch{bamToast("Couldn't reach the billing portal \u2014 check your connection and try again.", { tone: "error" })}
              }}
              style={{flex:1,padding:"11px 18px",borderRadius:8,border:`1px solid ${C.border}`,background:"transparent",
                color:C.text,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              <CreditCard size={14}/> Manage Billing
            </button>
            <button
              onClick={async ()=>{
                if(!(await bamConfirm({ title: "Cancel your membership?", message: "You'll keep access until the end of your current billing period.", confirmLabel: "Cancel membership", cancelLabel: "Keep membership", danger: true })))return;
                try{
                  assertNotStaging("The billing portal");
                  const {data:{session}}=await (await import("../../supabaseClient")).supabase.auth.getSession();
                  const token=session?.access_token;
                  const res=await fetch(
                    process.env.REACT_APP_PORTAL_URL||"https://rlfekxctjyvozujzbzwm.supabase.co/functions/v1/create-portal-session",
                    {method:"POST",headers:{"Content-Type":"application/json",...(token?{Authorization:`Bearer ${token}`}:{})},
                      body:JSON.stringify({returnUrl:window.location.href,flowType:"subscription_cancel"})}
                  );
                  if(res.ok){const{url}=await res.json();if(url)window.location.href=url;}
                  else{bamToast("Cancellation isn't available yet \u2014 email support@byanymeanscoaches.com", { tone: "error" })}
                }catch{bamToast("Cancellation isn't available yet \u2014 email support@byanymeanscoaches.com", { tone: "error" })}
              }}
              style={{padding:"11px 18px",borderRadius:8,border:"1px solid rgba(224,96,96,0.25)",background:"transparent",
                color:"#E06060",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
              Cancel
            </button>
          </div>
          <div style={{marginTop:10,fontSize:11,color:C.textDim,lineHeight:1.5,textAlign:"center"}}>
            <a href="mailto:support@byanymeanscoaches.com" style={{color:C.textMid,textDecoration:"none",borderBottom:"1px dotted "+C.textDim}}>support@byanymeanscoaches.com</a>
            {" \u00B7 "}
            <a href="#/refund" style={{color:C.textMid,textDecoration:"none",borderBottom:"1px dotted "+C.textDim}}>Refund policy</a>
          </div>
        </div>
        )}

        {/* BAM Consulting — only renders when there's something to show:
            either the user is in an org (sub status + manage link) or
            they're on the addon allowlist (marketing teaser to /programs).
            For everyone else, the section is suppressed entirely so we
            don't tease an unreleased product. */}
        {/* Suppress on native (iOS) — the "See plans" CTA links to a
            pricing surface (#/consulting) that would trip Apple's
            reader-app rules. */}
        {!isNative() && <ConsultingSection C={C} GOLD={GOLD}/>}

        {/* Help */}
        {sectionLabel("Help", Compass)}
        <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"18px",boxShadow:C.surfaceShadow,marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,color:C.text,fontWeight:600}}>Take the Tour</div>
              <div style={{fontSize:12,color:C.textDim,marginTop:3,lineHeight:1.5}}>A quick walkthrough of the platform - Dashboard, Community, Player Development, Team Coaching, Masterclasses, Resources, and the Toolbox.</div>
            </div>
            <button onClick={onStartTour}
              style={{flexShrink:0,padding:"10px 18px",borderRadius:8,border:`1px solid ${GOLD}`,background:"transparent",
                color:GOLD,fontSize:12,fontWeight:800,letterSpacing:1,cursor:"pointer",fontFamily:"'Bebas Neue',sans-serif",textTransform:"uppercase"}}>
              Start Tour
            </button>
          </div>
        </div>

        {/* Account */}
        {sectionLabel("Account", LogOut)}
        <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"18px",boxShadow:C.surfaceShadow,marginBottom:24}}>
          <button onClick={onSignOut}
            style={{width:"100%",padding:"12px 18px",borderRadius:8,border:"1px solid rgba(224,96,96,0.25)",background:"transparent",
              color:"#E06060",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            Sign Out
          </button>
          <button onClick={()=>{setDeleteText("");setDeleteError(null);setDeleteOpen(true);}}
            style={{width:"100%",marginTop:10,padding:"12px 18px",borderRadius:8,border:"none",background:"transparent",
              color:C.textDim,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>
            Delete Account
          </button>
        </div>

      </div>

      {deleteOpen && (
        <div style={{position:"fixed",inset:0,zIndex:6000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",
          display:"flex",alignItems:"center",justifyContent:"center",padding:"max(20px, env(safe-area-inset-top,0px)) 20px max(20px, env(safe-area-inset-bottom,0px))",fontFamily:"'DM Sans',sans-serif"}}
          onClick={()=>!deleteBusy&&setDeleteOpen(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:dark?"#141414":"#fff",border:`1px solid ${dark?"rgba(255,255,255,0.08)":C.border}`,borderRadius:16,
            padding:"24px 22px",width:"min(380px, calc(100vw - 40px))",maxHeight:"100%",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.6)"}}>
            <div style={{fontSize:18,fontWeight:800,color:C.text,marginBottom:8}}>Delete your account?</div>
            <div style={{fontSize:13.5,color:C.textDim,lineHeight:1.55,marginBottom:18}}>
              This permanently deletes your account, plans, uploads, and posts. <span style={{color:C.text,fontWeight:600}}>This cannot be undone.</span> If you have an active subscription, cancel it under Billing first. Type <span style={{color:"#E06060",fontWeight:700}}>DELETE</span> to confirm.
            </div>
            <input value={deleteText} onChange={e=>setDeleteText(e.target.value)} placeholder="DELETE" autoCapitalize="characters"
              style={{width:"100%",boxSizing:"border-box",padding:"11px 13px",borderRadius:9,background:dark?"#0E0E0E":"#f5f5f5",border:`1px solid ${C.border}`,color:C.text,fontSize:16,outline:"none",fontFamily:"inherit",marginBottom:14}}/>
            {deleteError && <div style={{fontSize:12.5,color:"#E06060",marginBottom:14,lineHeight:1.5}}>{deleteError}</div>}
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>!deleteBusy&&setDeleteOpen(false)} disabled={deleteBusy}
                style={{flex:1,padding:"12px",borderRadius:10,background:"transparent",color:C.textMid,border:`1px solid ${C.border}`,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              <button onClick={doDeleteAccount} disabled={deleteBusy || deleteText.trim().toUpperCase()!=="DELETE"}
                style={{flex:1,padding:"12px",borderRadius:10,border:"none",
                  background: (deleteBusy||deleteText.trim().toUpperCase()!=="DELETE") ? "rgba(224,96,96,0.3)" : "#E06060",
                  color:"#fff",fontSize:14,fontWeight:800,cursor:(deleteBusy||deleteText.trim().toUpperCase()!=="DELETE")?"not-allowed":"pointer",fontFamily:"inherit"}}>
                {deleteBusy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { IANA_TIMEZONES, getTzAbbr };

/**
 * NativeSubscriptionStatus — reader-app-safe Settings card for iOS.
 *
 * Shows only that the subscription is active and that billing is
 * managed on the web. No prices, no checkout buttons, no external
 * links — Apple rejects all three under the reader-app rules
 * (Guideline 3.1.3(a)).
 */
function NativeSubscriptionStatus({ C, GOLD }) {
  return (
    <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"18px 20px",boxShadow:C.surfaceShadow,marginBottom:24}}>
      <div style={{display:"flex",alignItems:"center",gap:14}}>
        <div style={{width:42,height:42,borderRadius:11,background:`${GOLD}15`,border:`1px solid ${GOLD}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <Shield size={20} color={GOLD}/>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:15,fontWeight:700,color:C.text}}>BAM Coaches Platform</div>
          <div style={{fontSize:12,color:"#5AB584",fontWeight:600,marginTop:3,display:"flex",alignItems:"center",gap:5}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:"#5AB584",display:"inline-block"}}/>
            Active
          </div>
        </div>
      </div>
      <div style={{fontSize:12,color:C.textDim,lineHeight:1.6,marginTop:14,paddingTop:14,borderTop:`1px solid ${C.border}`}}>
        Billing is managed on the web.
      </div>
    </div>
  );
}

/**
 * SubscriptionPanel — unified view of the coach's plan + add-ons.
 *
 * Replaces the previous separate "Subscription" card and "Add-ons"
 * section. One card now shows:
 *   - Base plan (BAM Coaches Platform · $34.99/mo) with active badge
 *   - Each active add-on inline as a sub-line (Film Studio+ · +$19.99/mo)
 *   - Total monthly bill at the bottom of the card
 *   - Manage Billing / Cancel buttons
 * Below the card, if the user is allowlisted to see private-beta
 * add-ons or there are public add-ons available, an "Available add-ons"
 * section renders cards for each inactive option.
 */
function SubscriptionPanel({ C, GOLD }) {
  const { addons, isVisible, loading } = useUserAddons();
  const visibleAddonKeys = ADDON_KEYS.filter(k => isVisible(k));
  const activeAddons = addons.filter(a => a.active);
  // Find the addon definitions for active add-ons so we can show prices.
  const activeAddonDefs = activeAddons
    .map(a => ({ active: a, def: ADDONS[a.addon_key] }))
    .filter(x => x.def);
  // Inactive but visible (so the coach can add them).
  const inactiveAddonKeys = visibleAddonKeys.filter(k => !addons.some(a => a.addon_key === k && a.active));

  // Base plan price — placeholder for now. Reading the real price
  // from Stripe / profiles is a future improvement (today the user
  // could be on Standard $34.99, EUFP-discounted $17.49, or the
  // legacy Pro tier at $54.99). For visual consistency we show the
  // standard price; the actual amount the user is billed is whatever
  // Stripe charges, which they can verify in the billing portal.
  const basePlanCents = 3499; // $34.99

  // Total = base + active addon monthly amounts (yearly addons billed
  // annually but still show their monthly equivalent in the breakdown).
  const totalCents = basePlanCents + activeAddonDefs.reduce((sum, x) => {
    // Pick whichever price interval the active sub item uses if we
    // know it; default to monthly for the breakdown view.
    return sum + (x.def.prices?.monthly?.amount_cents || 0);
  }, 0);
  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

  // Cancellation is the one moment we can still ask why: once they're in
  // Stripe's portal we only ever learn a flag flipped. The survey never
  // blocks — "continue" runs the exact same handoff.
  const [exitOpen, setExitOpen] = useState(false);

  const openPortal = async (flowType) => {
    try {
      assertNotStaging("The billing portal");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(
        process.env.REACT_APP_PORTAL_URL || "https://rlfekxctjyvozujzbzwm.supabase.co/functions/v1/create-portal-session",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ returnUrl: window.location.href, ...(flowType ? { flowType } : {}) }),
        }
      );
      if (res.ok) {
        const { url } = await res.json();
        if (url) { window.location.href = url; return; }
 bamToast("Couldn't open the billing portal right now. Please try again in a moment: if it keeps happening, email support@byanymeanscoaches.com.", { tone: "error" });
      } else {
 bamToast("Couldn't open the billing portal right now. Please try again in a moment: if it keeps happening, email support@byanymeanscoaches.com.", { tone: "error" });
      }
    } catch {
 bamToast("Couldn't reach the billing portal: check your connection and try again.", { tone: "error" });
    }
  };

  return (
    <>
      {exitOpen && (
        <ExitSurvey C={C} onClose={() => setExitOpen(false)}
          onContinue={() => { setExitOpen(false); openPortal("subscription_cancel"); }}
          onPaused={(res) => {
            setExitOpen(false);
            const back = res?.resumes_at ? new Date(res.resumes_at).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : "your return date";
 bamToast(`Paused. Billing and access resume on ${back}: your plans and saved drills will be right where you left them.`);
            // The paywall reads stripe_subscription_active, which the pause
            // just set false, so a reload lands them on the paused state
            // rather than a stale authorised session.
            setTimeout(() => { try { window.location.reload(); } catch {} }, 2200);
          }}/>
      )}
      {/* Unified plan card */}
      <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"20px",boxShadow:C.surfaceShadow,marginBottom:14}}>
        {/* Base plan row */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:10}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:42,height:42,borderRadius:11,background:`${GOLD}15`,border:`1px solid ${GOLD}30`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Shield size={20} color={GOLD}/>
            </div>
            <div>
              <div style={{fontSize:15,fontWeight:700,color:C.text}}>BAM Coaches Platform</div>
              <div style={{fontSize:12,color:"#5AB584",fontWeight:600,marginTop:3,display:"flex",alignItems:"center",gap:5}}>
                <span style={{width:6,height:6,borderRadius:"50%",background:"#5AB584",display:"inline-block"}}/>
                Active
              </div>
            </div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:"'Bebas Neue',sans-serif",letterSpacing:1}}>
              {fmt(basePlanCents)}<span style={{fontSize:12,color:C.textDim,fontWeight:400}}>/mo</span>
            </div>
          </div>
        </div>

        {/* Active add-ons inline. Each adds a row showing addon name +
            price. Empty list collapses gracefully (no "Add-ons" header
            when nothing's active). */}
        {activeAddonDefs.length > 0 && (
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,paddingBottom:12,display:"flex",flexDirection:"column",gap:8}}>
            <div style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.4,textTransform:"uppercase",marginBottom:2}}>
              Active add-ons
            </div>
            {activeAddonDefs.map(({active, def}) => (
              <div key={def.key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:13,gap:10}}>
                <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                  <Sparkles size={13} color={GOLD} style={{flexShrink:0}}/>
                  <span style={{color:C.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{def.name}</span>
                </div>
                <span style={{color:C.textMid,fontWeight:600,flexShrink:0}}>{def.prices?.monthly?.display ? `+${def.prices.monthly.display}` : "Included"}</span>
              </div>
            ))}
          </div>
        )}

        {/* Total monthly bill — only render when there's at least one
            add-on. With just the base plan, the price already shows
            in the header so this would be redundant. */}
        {activeAddonDefs.length > 0 && (
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <span style={{fontSize:11,fontWeight:700,color:C.textDim,letterSpacing:.5,textTransform:"uppercase"}}>Total · monthly</span>
            <span style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:"'Bebas Neue',sans-serif",letterSpacing:1}}>{fmt(totalCents)}<span style={{fontSize:11,color:C.textDim,fontWeight:400,fontFamily:"inherit"}}>/mo</span></span>
          </div>
        )}

        {/* Manage Billing / Cancel — same as before. */}
        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14,marginTop:12,display:"flex",gap:10}}>
          <button
            onClick={() => openPortal()}
            style={{flex:1,padding:"11px 18px",borderRadius:8,border:`1px solid ${C.border}`,background:"transparent",
              color:C.text,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
              display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            <CreditCard size={14}/> Manage Billing
          </button>
          <button
            onClick={async () => {
              if(!(await bamConfirm({ title: "Cancel your membership?", message: "You'll keep access until the end of your current billing period.", confirmLabel: "Cancel membership", cancelLabel: "Keep membership", danger: true })))return;
              setExitOpen(true);
            }}
            style={{padding:"11px 18px",borderRadius:8,border:"1px solid rgba(224,96,96,0.25)",background:"transparent",
              color:"#E06060",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
            Cancel
          </button>
        </div>
        <div style={{marginTop:10,fontSize:11,color:C.textDim,lineHeight:1.5,textAlign:"center"}}>
          <a href="mailto:support@byanymeanscoaches.com" style={{color:C.textMid,textDecoration:"none",borderBottom:"1px dotted "+C.textDim}}>support@byanymeanscoaches.com</a>
          {" · "}
          <a href="#/refund" style={{color:C.textMid,textDecoration:"none",borderBottom:"1px dotted "+C.textDim}}>Refund policy</a>
        </div>
      </div>

      {/* Available add-ons — only render the section if there's at
          least one inactive but visible addon to offer. */}
      {!loading && inactiveAddonKeys.length > 0 && (
        <AvailableAddonsSection
          C={C}
          GOLD={GOLD}
          inactiveAddonKeys={inactiveAddonKeys}
        />
      )}
    </>
  );
}

/**
 * AddonsSection (legacy, no longer rendered) — kept temporarily for
 * reference. The unified SubscriptionPanel above replaced it.
 * Each card mirrors a row in `user_addons` (active or not). Toggling a
 * card calls the manage-addon edge function which writes to Stripe;
 * the stripe-webhook then reconciles the user_addons mirror table.
 *
 * Beta gating: addons in private beta (see addons.js
 * `addonsLockedToAllowlist`) only render for allowlisted emails. The
 * server enforces the same allowlist; this is just to keep the UI
 * tidy until launch.
 */
function AvailableAddonsSection({ C, GOLD, inactiveAddonKeys }) {
  const { addons, has, refresh } = useUserAddons();
  const [busy, setBusy] = useState(null);   // addon_key currently being toggled
  const [error, setError] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null); // {addonKey, interval}

  const visibleAddons = inactiveAddonKeys || [];
  if (visibleAddons.length === 0) return null;

  const sectionLabel = (text, IconComp) => (
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,marginTop:6}}>
      <IconComp size={14} color={GOLD}/>
      <span style={{fontSize:11,fontWeight:800,color:GOLD,letterSpacing:1.4,textTransform:"uppercase"}}>{text}</span>
    </div>
  );

  const toggle = async (addonKey, action, interval) => {
    if (busy) return;
    setBusy(addonKey);
    setError(null);
    try {
      assertNotStaging("Add-ons");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL || "https://rlfekxctjyvozujzbzwm.supabase.co"}/functions/v1/manage-addon`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ addon_key: addonKey, action, interval }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `manage-addon ${res.status}`);
      // Quick refresh — the webhook will reconcile authoritatively
      // shortly after, but the manage-addon optimistic write means
      // user_addons should already reflect the change.
      await refresh();
    } catch (e) {
      // Turn raw Stripe/server errors into something a coach can act on.
      const raw = (e?.message || "").toLowerCase();
      if (raw.includes("card") || raw.includes("payment") || raw.includes("declin") || raw.includes("requires_action") || raw.includes("authentication") || raw.includes("3d")) {
        setError("Your payment method couldn't be charged. Update your card under Billing below, then try again.");
      } else if (raw.includes("customer") || raw.includes("no stripe") || raw.includes("not found")) {
        setError("No payment method on file. Add one under Billing below to purchase add-ons.");
      } else {
 setError("Couldn't update the add-on: please try again. If it keeps happening, email support@byanymeanscoaches.com.");
      }
    } finally {
      setBusy(null);
      setConfirmFor(null);
    }
  };

  return (
    <>
      {sectionLabel("Available add-ons", Sparkles)}
      <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:24}}>
        {error && (
          <div style={{padding:"10px 14px",background:"rgba(224,96,96,0.10)",border:"1px solid rgba(224,96,96,0.30)",borderRadius:10,fontSize:12,color:"#ff9d9d",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
            <span style={{flex:1,minWidth:0}}>{error}</span>
            <button onClick={()=>setError(null)} aria-label="Dismiss" style={{background:"transparent",border:"none",color:"#ff9d9d",cursor:"pointer",fontSize:14,padding:4}}>×</button>
          </div>
        )}

        {visibleAddons.map(key => {
          const addon = ADDONS[key];
          // Not every visible addon is purchasable. Private-beta addons
          // (auto_afs) ship with `prices: {}` until pricing is locked, so
          // these can be undefined — read them defensively and render a
          // beta notice instead of a buy button when there's no price.
          const monthly = addon?.prices?.monthly || null;
          const yearly = addon?.prices?.yearly || null;
          const active = has(key);
          const activeRow = addons.find(a => a.addon_key === key && a.active);
          const isBusy = busy === key;
          return (
            <div key={key} style={{
              background: C.cardGradient,
              border: `1px solid ${active ? GOLD + "55" : C.cardBorder}`,
              borderRadius: 14,
              padding: "18px 20px",
              boxShadow: active ? `${C.surfaceShadow}, 0 0 0 1px ${GOLD}10` : C.surfaceShadow,
              position: "relative",
            }}>
              {/* Active badge */}
              {active && (
                <div style={{position:"absolute",top:14,right:14,display:"flex",alignItems:"center",gap:5,fontSize:10,fontWeight:800,color:"#5AB584",background:"rgba(90,181,132,0.10)",padding:"3px 9px",borderRadius:999,letterSpacing:.5,textTransform:"uppercase"}}>
                  <Check size={10}/> Active
                </div>
              )}

              <div style={{display:"flex",alignItems:"flex-start",gap:14,marginBottom:10}}>
                <div style={{width:40,height:40,borderRadius:10,background:`${GOLD}18`,border:`1px solid ${GOLD}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <Sparkles size={18} color={GOLD}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:2}}>
                    {addon.name}
                  </div>
                  <div style={{fontSize:12,color:C.textMid,fontWeight:600}}>
                    {addon.tagline}
                  </div>
                </div>
              </div>

              <div style={{fontSize:13,color:C.textMid,lineHeight:1.55,marginBottom:14}}>
                {addon.description}
              </div>

              <ul style={{margin:"0 0 16px 0",padding:0,listStyle:"none",display:"flex",flexDirection:"column",gap:6}}>
                {(addon.bullets || []).map((b, i) => (
                  <li key={i} style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:12.5,color:C.textMid,lineHeight:1.5}}>
                    <Check size={13} color={GOLD} style={{flexShrink:0,marginTop:3}}/>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              {!active && monthly ? (
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <button
                    onClick={() => setConfirmFor({ addonKey: key, interval: "month" })}
                    disabled={isBusy}
                    style={{
                      flex:1,
                      padding:"11px 18px",
                      borderRadius:10,
                      background: `linear-gradient(180deg, ${GOLD} 0%, #d4ce8a 100%)`,
                      color:"#111",
                      border:"none",
                      fontSize:13,
                      fontWeight:800,
                      cursor: isBusy ? "wait" : "pointer",
                      opacity: isBusy ? .6 : 1,
                      fontFamily:"inherit",
                      letterSpacing:.3,
                      display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                    }}>
                    {isBusy ? <Loader2 size={14} style={{animation:"spin 1s linear infinite"}}/> : <Sparkles size={14}/>}
                    Add for {monthly.display}
                  </button>
                  {yearly && (
                    <button
                      onClick={() => setConfirmFor({ addonKey: key, interval: "year" })}
                      disabled={isBusy}
                      style={{
                        padding:"11px 16px",
                        borderRadius:10,
                        background:"transparent",
                        color:GOLD,
                        border:`1px solid ${GOLD}40`,
                        fontSize:12,
                        fontWeight:700,
                        cursor: isBusy ? "wait" : "pointer",
                        opacity: isBusy ? .6 : 1,
                        fontFamily:"inherit",
                        whiteSpace:"nowrap",
                      }}>
                      Annual ({yearly.display})
                    </button>
                  )}
                </div>
              ) : !active ? (
                <div style={{display:"flex",alignItems:"center",gap:8,paddingTop:10,borderTop:`1px solid ${C.border}`,fontSize:11.5,color:C.textDim,fontWeight:600}}>
                  <Sparkles size={12} color={GOLD}/>
 Private beta: pricing not set yet. You already have access.
                </div>
              ) : (
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,paddingTop:10,borderTop:`1px solid ${C.border}`}}>
                  <div style={{fontSize:11,color:C.textDim}}>
                    Active{activeRow?.started_at ? ` since ${new Date(activeRow.started_at).toLocaleDateString()}` : ""}
                  </div>
                  <button
                    onClick={() => setConfirmFor({ addonKey: key, action: "remove" })}
                    disabled={isBusy}
                    style={{
                      padding:"7px 14px",
                      borderRadius:8,
                      background:"transparent",
                      color:"#E06060",
                      border:"1px solid rgba(224,96,96,0.3)",
                      fontSize:11,
                      fontWeight:700,
                      cursor: isBusy ? "wait" : "pointer",
                      opacity: isBusy ? .6 : 1,
                      fontFamily:"inherit",
                    }}>
                    {isBusy ? "…" : "Remove"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Confirmation modal — covers both "add at $price" and "remove"
          flows. Always show the proration warning so the coach knows
          what's about to be charged. */}
      {confirmFor && (
        <div role="dialog" aria-modal="true" onClick={() => !busy && setConfirmFor(null)}
          style={{position:"fixed",inset:0,zIndex:5500,background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"center",justifyContent:"center",
            padding:"max(20px, env(safe-area-inset-top,0px)) 20px max(20px, env(safe-area-inset-bottom,0px))"}}>
          <div onClick={e => e.stopPropagation()} className="bam-settings-card" style={{
            background: "linear-gradient(180deg,#151515,#0E0E0E)",
            border: `1px solid ${C.cardBorder}`,
            borderRadius: 14,
            padding: "26px 28px",
            maxWidth: 420,
            width: "100%",
            maxHeight: "100%",
            overflowY: "auto",
            boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
          }}>
            {(() => {
              const isRemove = confirmFor.action === "remove";
              const addon = ADDONS[confirmFor.addonKey];
              const price = isRemove ? null : addon?.prices?.[confirmFor.interval === "year" ? "yearly" : "monthly"];
              return (
                <>
                  <h3 style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:24,letterSpacing:1.5,color:C.text,margin:"0 0 12px"}}>
                    {isRemove ? `Remove ${addon?.name}?` : `Add ${addon?.name}?`}
                  </h3>
                  {!isRemove && price && (
                    <p style={{fontSize:14,color:C.textMid,lineHeight:1.6,margin:"0 0 12px"}}>
                      You'll be charged <strong style={{color:C.text}}>{price.display}</strong>, prorated for the rest of your current billing period. After that, it bills automatically until you remove it.
                    </p>
                  )}
                  {isRemove && (
                    <p style={{fontSize:14,color:C.textMid,lineHeight:1.6,margin:"0 0 12px"}}>
                      You'll get a prorated credit for the unused time on this billing period. The add-on stays active until the end of the period, then stops billing.
                    </p>
                  )}
                  <p style={{fontSize:12,color:C.textDim,lineHeight:1.5,margin:"0 0 22px"}}>
                    All charges via Stripe. You can re-add or remove at any time.
                  </p>
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <button onClick={() => setConfirmFor(null)} disabled={busy === confirmFor.addonKey}
                      style={{padding:"9px 18px",borderRadius:8,fontSize:13,fontWeight:600,background:"transparent",color:C.textMid,border:`1px solid ${C.border}`,cursor:"pointer",fontFamily:"inherit"}}>
                      Cancel
                    </button>
                    <button
                      onClick={() => toggle(confirmFor.addonKey, isRemove ? "remove" : "add", confirmFor.interval)}
                      disabled={busy === confirmFor.addonKey}
                      style={{
                        padding:"9px 22px",
                        borderRadius:8,
                        fontSize:13,
                        fontWeight:800,
                        background: isRemove ? "#E06060" : GOLD,
                        color: isRemove ? "#fff" : "#111",
                        border:"none",
                        cursor: busy === confirmFor.addonKey ? "wait" : "pointer",
                        fontFamily:"inherit",
                        letterSpacing:.3,
                        display:"flex",alignItems:"center",gap:6,
                      }}>
                      {busy === confirmFor.addonKey && <Loader2 size={13} style={{animation:"spin 1s linear infinite"}}/>}
                      {isRemove ? "Remove" : "Confirm"}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * SecurityPanel — Face ID / Touch ID re-auth opt-in for the iOS app.
 *
 * Behavior when toggled on:
 *   1. Verify the user with Face ID right now (fail-fast — if the system
 *      prompt is denied or unavailable we never set the pref).
 *   2. Stash the active Supabase session in the iOS Keychain so a future
 *      app-resume Face ID prompt has tokens to restore.
 *   3. Set the localStorage flag `bam_face_id_enabled=1` that nativeInit
 *      checks on every app foreground.
 *
 * Toggling off clears the keychain stash and the flag.
 */
function SecurityPanel({ C, dark }) {
  const FACE_ID_PREF = "bam_face_id_enabled";
  const [enabled, setEnabled] = useState(false);
  const [available, setAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await biometricAvailable();
        if (cancelled) return;
        setAvailable(!!r.available);
        setBiometryType(r.biometryType);
      } catch {}
      try {
        if (!cancelled) setEnabled(localStorage.getItem(FACE_ID_PREF) === "1");
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // biometricAvailable() returns the human label directly ("Face ID",
  // "Touch ID", "Fingerprint", etc.). Default to "Face ID" if unknown
  // since that's the most common iPhone case.
  const label = biometryType || "Face ID";

  const onChange = async (next) => {
    if (busy) return;
    setError(null);
    if (!available && next) {
      setError(`${label} isn't available on this device.`);
      return;
    }
    setBusy(true);
    try {
      if (next) {
        const ok = await biometricVerify({ reason: `Enable ${label} for BAM Coaches` });
 if (!ok) { setError("Couldn't verify: try again."); setBusy(false); return; }
        try { localStorage.setItem(FACE_ID_PREF, "1"); } catch {}
        await haptic("success");
        setEnabled(true);
      } else {
        try { localStorage.removeItem(FACE_ID_PREF); } catch {}
        await haptic("light");
        setEnabled(false);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{padding:"4px 0"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",minHeight:44}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,color:C.text,fontWeight:500}}>Use {label} to unlock</div>
          <div style={{fontSize:11,color:C.textDim,marginTop:3,lineHeight:1.5}}>
            {available
              ? `Prompt for ${label} when you reopen the app. Faster than typing your password every time.`
              : "This device doesn't have Face ID or Touch ID set up."}
          </div>
        </div>
        <div onClick={() => onChange(!enabled)}
          style={{width:44,height:24,borderRadius:12,background:enabled?GOLD:dark?"#333":C.border,position:"relative",flexShrink:0,cursor:available?"pointer":"not-allowed",opacity:available?1:0.4,transition:"background .15s"}}>
          <div style={{position:"absolute",top:4,left:4,width:15,height:15,borderRadius:"50%",background:enabled?(dark?"#0A0A0A":"#fff"):(dark?"#666":"#fff"),transform:enabled?"translateX(20px)":"translateX(0)",transition:"transform .15s",boxShadow:"0 1px 5px rgba(0,0,0,.3)"}}/>
        </div>
      </div>
      {busy && (
        <div style={{fontSize:11,color:C.textDim,padding:"6px 0",display:"flex",alignItems:"center",gap:6}}>
          <Loader2 size={12} style={{animation:"spin 1s linear infinite"}}/> Verifying…
        </div>
      )}
      {error && (
        <div style={{fontSize:11,color:"#ff9d9d",padding:"6px 0"}}>{error}</div>
      )}
    </div>
  );
}

// ── Helpers shared inside SettingsPage ──────────────────────────────────

function settingsSectionLabel(label, Icon, GOLD) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:10,marginTop:18}}>
      <Icon size={14} color={GOLD}/>
      <span style={{fontSize:11,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase"}}>{label}</span>
    </div>
  );
}

/**
 * ConsultingSection — surfaces BAM Consulting status in Settings.
 *
 * Three states:
 *   1. User has an org membership → show org name, current tier (or "no
 *      sub" if dormant), and a "Manage" link to /org.
 *   2. User is on the addon allowlist (programs beta) → show a teaser
 *      that links to /programs.
 *   3. Otherwise → render nothing. We don't tease an unreleased product
 *      to the general population.
 */
function ConsultingSection({ C, GOLD }) {
  const { orgs, currentOrg, consultingSubscription, partnerProgress, loading } = useOrg();
  const { userEmail } = useUserAddons();

  if (loading) return null;
  const hasOrg = orgs && orgs.length > 0 && currentOrg;
  const showTeaser = !hasOrg && (!consultingLockedToAllowlist || isAllowedBetaUser(userEmail));

  if (!hasOrg && !showTeaser) return null;

  if (hasOrg) {
    const tierDef = consultingSubscription?.tier ? CONSULTING_TIERS[consultingSubscription.tier] : null;
    return (
      <>
        {settingsSectionLabel("BAM Consulting", Building2, GOLD)}
        <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:"18px 20px",marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <div style={{width:40,height:40,borderRadius:10,background:`${GOLD}18`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
              {currentOrg.logo_url
                ? <img src={currentOrg.logo_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                : <Building2 size={18} color={GOLD}/>}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontSize:14,fontWeight:700,color:C.text}}>{currentOrg.name}</span>
                {currentOrg.is_partner && <PartnerOrgBadge size="sm" since={currentOrg.partner_since}/>}
              </div>
              <div style={{fontSize:11.5,color:C.textDim,marginTop:3}}>
 {tierDef ? `${tierDef.name.replace(/^BAM Consulting: /, "")} · ${consultingSubscription.status}` : "No active consulting plan"}
                {consultingSubscription && !currentOrg.is_partner && partnerProgress.monthsToGo > 0 && (
                  <> · {partnerProgress.monthsToGo} mo to BAM Partner</>
                )}
              </div>
            </div>
            <button onClick={() => { window.location.hash = "#/org"; }}
              style={{padding:"7px 14px",borderRadius:8,background:`${GOLD}14`,border:`1px solid ${GOLD}55`,color:GOLD,fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",letterSpacing:.3,display:"inline-flex",alignItems:"center",gap:5}}>
              Manage <ArrowRight size={11}/>
            </button>
          </div>
        </div>
      </>
    );
  }

  // Teaser for allowlist users without an org
  return (
    <>
      {settingsSectionLabel("BAM Consulting", Building2, GOLD)}
      <div style={{background:C.cardGradient,border:`1px dashed ${GOLD}44`,borderRadius:14,padding:"16px 20px",marginBottom:24}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <Sparkles size={18} color={GOLD} style={{flexShrink:0}}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13.5,fontWeight:700,color:C.text}}>Long-term coaching partnership</div>
 <div style={{fontSize:11.5,color:C.textDim,marginTop:3,lineHeight:1.5}}>For clubs, schools, academies: strategy support + a path to BAM Partner Org status.</div>
          </div>
          <button onClick={() => { window.location.hash = "#/consulting"; }}
            style={{padding:"7px 14px",borderRadius:8,background:GOLD,border:"none",color:"#111",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",letterSpacing:.3,display:"inline-flex",alignItems:"center",gap:5}}>
            See plans <ArrowRight size={11}/>
          </button>
        </div>
      </div>
    </>
  );
}
